import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
  fetchIndividualDetail,
  updateServices,
  toggleService,
  uploadDocument,
  removeDocument,
  fetchDocuments,
  setUploadModalOpen,
  setDocumentsPage,
  setDocumentsPageSize,
  clearDetail,
  optimisticallyToggleService,
  revertServiceToggle,
  updateDocumentExpiresAt,
} from "./individual-detail.slice";
import { uuid } from "@/interfaces";
import { toast } from "sonner";

export function useIndividualDetail(orgIndividualId?: string) {
  const dispatch = useAppDispatch();
  const {
    detail,
    loading,
    error,
    documents,
    documentsLoading,
    documentsTotal,
    documentsPage,
    documentsPageSize,
    uploadModalOpen,
    uploadingDocument,
    uploadError,
    updatingDocumentId,
    savingServices,
    serviceError,
  } = useAppSelector((state) => state.individualDetail);

  useEffect(() => {
    if (orgIndividualId) {
      dispatch(fetchIndividualDetail(orgIndividualId as uuid));
    }

    return () => {
      dispatch(clearDetail());
    };
  }, [orgIndividualId, dispatch]);

  const handleUpdateServices = async (services: uuid[]) => {
    if (!orgIndividualId) return;

    try {
      await dispatch(
        updateServices({
          orgIndividualId: orgIndividualId as uuid,
          services,
        })
      ).unwrap();
      toast.success("Services updated successfully");
    } catch (error: any) {
      toast.error(error.message ?? "Failed to update services");
    }
  };

  const handleToggleService = async (serviceId: string, isAdding: boolean) => {
    if (!orgIndividualId || !detail) return;

    const currentServices = detail.serviceIds;
    const previousServices = [...currentServices];

    dispatch(optimisticallyToggleService({ serviceId, isAdding }));

    try {
      await dispatch(
        toggleService({
          orgIndividualId: orgIndividualId as uuid,
          serviceId,
          currentServices: previousServices,
        })
      ).unwrap();

    } catch (error: any) {
      dispatch(revertServiceToggle({ serviceId, previousServices }));
      toast.error(`Failed to ${isAdding ? "add" : "remove"} service`);
    }
  };

  const handleDocumentUpload = async (file: File, expiresAt?: string | Date) => {
    if (!orgIndividualId) return;

    try {
      await dispatch(
        uploadDocument({
          orgIndividualId: orgIndividualId as uuid,
          file,
          expiresAt,
        })
      ).unwrap();
      toast.success("Document uploaded successfully");
    } catch (error: any) {
      toast.error(error.message ?? "Failed to upload document");
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      await dispatch(removeDocument(docId)).unwrap();
      toast.success("Document deleted successfully");
    } catch (error: any) {
      toast.error(error.message ?? "Failed to delete document");
    }
  };

  const handleUpdateDocumentExpiresAt = async (docId: string, expiresAt: string | Date | null) => {
    try {
      await dispatch(updateDocumentExpiresAt({ docId, expiresAt })).unwrap();
      toast.success("Document expiration updated successfully");
    } catch (error: any) {
      toast.error(error.message ?? "Failed to update document expiration");
      throw error;
    }
  };

  const setUploadDocumentModalOpen = (open: boolean) => {
    dispatch(setUploadModalOpen(open));
  };

  const setDocPage = (page: number) => {
    dispatch(setDocumentsPage(page));
    if (orgIndividualId) {
      dispatch(
        fetchDocuments({
          orgIndividualId: orgIndividualId as uuid,
          page,
          pageSize: documentsPageSize,
        })
      );
    }
  };

  const setDocPageSize = (pageSize: number) => {
    dispatch(setDocumentsPageSize(pageSize));
    if (orgIndividualId) {
      dispatch(
        fetchDocuments({
          orgIndividualId: orgIndividualId as uuid,
          page: documentsPage,
          pageSize,
        })
      );
    }
  };

  return {
    detail,
    loading,
    error,
    documents,
    documentsLoading,
    documentsTotal,
    documentsPage,
    documentsPageSize,
    uploadModalOpen,
    uploadingDocument,
    uploadError,
    updatingDocumentId,
    savingServices,
    serviceError,
    updateServices: handleUpdateServices,
    toggleService: handleToggleService,
    uploadDocument: handleDocumentUpload,
    deleteDocument: handleDeleteDocument,
    updateDocumentExpiresAt: handleUpdateDocumentExpiresAt,
    setUploadDocumentModalOpen,
    setDocPage,
    setDocPageSize,
  };
}
